import { formatDate } from "date-fns"
import { t } from "i18next"
import { Banner } from "@/components/ui/banner"
import {
  Card,
  CardBody,
  CardBodyContent,
  CardBodyHeader,
  CardBodyHeaderTitle,
  CardHeader,
  CardHeaderTitle,
} from "@/components/ui/card.tsx"
import { DateOnly } from "@/lib/date-only"
import { DD_MM_YYYY } from "@/lib/format-date"
import { joinStrings } from "@/lib/joinString"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"

export interface CoverageProps extends React.HTMLAttributes<HTMLElement> {
  id: string
  insureeId: string
  organisationId: string
  organisationName: string
  insuranceCardNumber: string
  insureeNumber: string
  validFrom: string
  validTo: string
  updatedAt: string
  isPrimary: boolean
  policyNumber: string
  productId: string
  productName: string
  shouldContainInsuranceCardNumber: boolean | null
  children?: React.ReactNode
}

const Coverage = ({
  coverages,
  activeCoverages,
  children,
}: {
  coverages: CoverageProps[]
  activeCoverages: CoverageProps[]
  children?: React.ReactNode
}) => {
  const countryCode = useCountryParam()

  const displayedCoverages = activeCoverages.length > 0 ? activeCoverages : coverages

  const getWarningMessage = (coverage: CoverageProps) => {
    const { validTo, validFrom, insuranceCardNumber, insureeNumber } = coverage

    const currentDate = new DateOnly(new Date())

    if (validTo) {
      const coverageEndDate = new DateOnly(validTo)
      const isOutdated = currentDate.isGreaterThan(coverageEndDate)
      if (isOutdated) return t("patient.information.coverage.warning.outdatedCoverage")
    }

    if (countryCode === "CH" && !insureeNumber && !insuranceCardNumber)
      return t("patient.information.coverage.warning.missingVekaNumber")

    if (validFrom) {
      const coverageStartDate = new DateOnly(validFrom)
      const hasNotStarted = coverageStartDate.isGreaterThan(currentDate)
      if (hasNotStarted) return t("patient.information.coverage.warning.invalidCoverage")
    }
  }

  return (
    <Card className="animate-fade-in-top w-full" data-testid="patient-information-coverage">
      <CardHeader>
        <div className="flex w-full items-center justify-between gap-4">
          <CardHeaderTitle data-testid="card-header-title-content">
            {t("patient.create.steps.COVERAGE")}
          </CardHeaderTitle>
          {children}
        </div>
      </CardHeader>
      <CardBody>
        {displayedCoverages.map((coverage, idx) => {
          const {
            organisationName,
            insuranceCardNumber,
            insureeNumber,
            validFrom,
            validTo,
            updatedAt,
            isPrimary,
            productName,
            policyNumber,
          } = coverage

          const warningMessage = getWarningMessage(coverage)
          const formattedValidFrom = validFrom ? formatDate(new Date(validFrom), DD_MM_YYYY) : ""
          const formattedValidTo = validTo ? formatDate(new Date(validTo), DD_MM_YYYY) : ""
          const key = organisationName + idx
          const testId = isPrimary ? "selected-compulsory" : "selected-supplementary"

          return (
            <div key={key}>
              <CardBodyHeader>
                <CardBodyHeaderTitle data-testid={testId}>
                  {isPrimary
                    ? t(`patient.information.insurance.compulsory.compulsory`)
                    : t("patient.information.insurance.supplementary.supplementaryTitle")}
                  {updatedAt &&
                    ` ${t("patient.information.lastUpdateOn", {
                      updateAt: formatDate(new Date(updatedAt), DD_MM_YYYY),
                    })}`}
                </CardBodyHeaderTitle>
              </CardBodyHeader>
              <CardBodyContent>
                <div className="flex w-full justify-between">
                  <div className="w-1/2">
                    <div className="text-on-surface-subdued text-xs">{t("common.name")}</div>
                    <div
                      data-testid="selected-health-insurance"
                      className="text-on-surface text-sm">{`${organisationName} (${productName})`}</div>
                  </div>
                  <div className="w-1/2">
                    <div className="text-on-surface-subdued text-xs">{t("common.valid")}</div>
                    <div className="text-on-surface text-sm">
                      {joinStrings([formattedValidFrom, formattedValidTo], " - ")}
                    </div>
                  </div>
                </div>
              </CardBodyContent>
              <CardBodyContent>
                <div className="flex w-full justify-between">
                  {countryCode === "CH" && (
                    <>
                      <div className="w-1/2">
                        <div className="text-on-surface-subdued text-xs">
                          {t("patient.information.insuranceCardNumber")}
                        </div>
                        <div data-testid="selected-insurance-number" className="text-on-surface text-sm">
                          {insuranceCardNumber}
                        </div>
                      </div>
                      <div className="w-1/2">
                        <div className="text-on-surface-subdued text-xs">{t("patient.information.memberNumber")}</div>
                        <div className="text-on-surface text-sm">{insureeNumber}</div>
                      </div>
                    </>
                  )}
                  {countryCode === "DE" && policyNumber ? (
                    <div className="w-1/2">
                      <div className="text-on-surface-subdued text-xs">{t("patient.information.policyNumber")}</div>
                      <div data-testid="selected-policy-number" className="text-on-surface text-sm">
                        {policyNumber}
                      </div>
                    </div>
                  ) : null}
                  {countryCode === "DE" && insureeNumber ? (
                    <div className="w-1/2">
                      <div className="text-on-surface-subdued text-xs">{t("patient.information.insureeNumber")}</div>
                      <div data-testid="selected-insuree-number" className="text-on-surface text-sm">
                        {insureeNumber}
                      </div>
                    </div>
                  ) : null}
                </div>
              </CardBodyContent>
              {warningMessage && (
                <CardBodyContent>
                  <Banner variant="critical" type="inline">
                    {warningMessage}
                  </Banner>
                </CardBodyContent>
              )}
            </div>
          )
        })}
      </CardBody>
    </Card>
  )
}
export { Coverage }
